import { useFormContext } from 'react-hook-form'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../common/integrations'
import { IntegrationSettings } from '../../schema'
import { useMemo, useState } from 'react'
import { SelectDropdown, SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { Col, Container, Row } from 'react-bootstrap'
import { IntegrationIcon } from '../../../../../../components/IntegrationIcon'
import { Button } from '../../../../../../components/Button/Button'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../store/hooks'
import { useGetDescribeQuery } from '../../../../../../store/integrations/integrations'
import { FormFieldDefinition } from '../../hooks'
import { DetailedCRMAccountIntegrationMapping } from '../../../../../../models/Account'

interface AdditionalFieldSelectorProps {
  serviceId: IntegrationServiceId
  mappingObjectName: string
  mappingObjectIndex?: number
  ignoreKeys?: string[]
  alreadyAddedFields?: string[]
  title: string
  description: string
}

export const AdditionalFieldSelector: React.FC<AdditionalFieldSelectorProps> = ({
  serviceId,
  mappingObjectName,
  ignoreKeys,
  alreadyAddedFields,
  title,
  description,
  mappingObjectIndex,
}) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const mappings = watch('mappings')
  const mappingValue = mappings?.[mappingObjectName as keyof typeof mappings]
  const currentMapping = mappingObjectIndex !== undefined && Array.isArray(mappingValue) ? mappingValue[mappingObjectIndex] : mappingValue

  const hasFields = currentMapping && 'fields' in currentMapping
  const hasObjectName = currentMapping && 'objectName' in currentMapping

  const objectFields = hasFields ? currentMapping.fields || {} : {}
  const currentObjectName = hasObjectName ? currentMapping.objectName : null
  const accountId = useAppSelector(selectAccountId)

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: currentObjectName! },
    },
    { skip: !currentObjectName || !accountId },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  const [tempRows, setTempRows] = useState<string[]>([])

  const fieldOptions = useMemo(
    () =>
      currentObjectFormFieldDefinitions
        .filter((field: FormFieldDefinition) => !(alreadyAddedFields || []).includes(field.name))
        .map((field: { name: string; label: string }): SelectOption => {
          return {
            id: field.name,
            label: `${field.label} (${field.name})`,
          }
        }) || [],
    [currentObjectFormFieldDefinitions, alreadyAddedFields],
  )

  const additionalFieldKeys = Object.keys(objectFields).filter((key) => !(ignoreKeys || []).includes(key))

  const handleAddField = () => {
    setTempRows((prev) => [...prev, crypto.randomUUID()])
  }

  const handleRemoveTempRow = (id: string) => {
    setTempRows((prev) => prev.filter((rowId) => rowId !== id))
  }

  const handleRemoveSavedField = (key: string) => {
    const newFields = { ...objectFields }
    delete newFields[key]
    const updatedMappings =
      mappingObjectIndex !== undefined && Array.isArray(mappings[mappingObjectName as keyof typeof mappings])
        ? {
            ...mappings,
            [mappingObjectName]: (mappings[mappingObjectName as keyof typeof mappings] as DetailedCRMAccountIntegrationMapping[]).map((mapping, idx) =>
              idx === mappingObjectIndex ? { ...mapping, fields: newFields } : mapping,
            ),
          }
        : {
            ...mappings,
            [mappingObjectName]: {
              ...currentMapping,
              fields: newFields,
            },
          }
    setValue('mappings', updatedMappings, { shouldDirty: true, shouldValidate: true })
  }

  const handleFieldSelect = (selectedOption: SelectOption, tempRowId?: string, oldKey?: string) => {
    const newKey = selectedOption.id
    const newDescriptors = [{ type: 'field' as const, value: newKey }]

    const updatedFields = { ...objectFields }

    if (oldKey && oldKey !== newKey) {
      delete updatedFields[oldKey]
    }

    updatedFields[newKey] = newDescriptors

    const updatedObject = {
      ...currentMapping,
      fields: updatedFields,
    }

    const updatedMappingValue =
      mappingObjectIndex !== undefined && Array.isArray(mappingValue)
        ? mappingValue.map((item, index) => (index === mappingObjectIndex ? updatedObject : item))
        : updatedObject

    const updatedMappings = {
      ...mappings,
      [mappingObjectName]: updatedMappingValue,
    }

    setValue('mappings', updatedMappings, { shouldDirty: true, shouldValidate: true })

    if (tempRowId) {
      handleRemoveTempRow(tempRowId)
    }
  }

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4 className='mb-2'>{title}</h4>
          <p className='zocks-text-gray-600'>{description}</p>
        </Col>
      </Row>
      <Row className='mb-2 fw-bold'>
        <Col className='d-flex align-items-center'>
          <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
          <span className='ms-2'>{getIntegrationDisplayName(serviceId).toUpperCase()} FIELD</span>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className='d-flex flex-column gap-2 mb-3'>
            {additionalFieldKeys.map((key: string) => {
              const currentFieldFormFieldDefinition = currentObjectFormFieldDefinitions.find((field) => field.name === key) || { name: key, label: key }
              const currentFieldOptions = [
                ...fieldOptions,
                {
                  id: currentFieldFormFieldDefinition.name,
                  label: `${currentFieldFormFieldDefinition.label} (${currentFieldFormFieldDefinition.name})`,
                },
              ]
              const currentOption = currentFieldOptions.find((opt: SelectOption) => opt.id === key) || { id: key, label: key }

              return (
                <div key={key} className='d-flex align-items-center gap-2'>
                  <div className='flex-grow-1'>
                    <SelectDropdown
                      options={currentFieldOptions}
                      initialSelectedOption={currentOption}
                      onOptionSelected={(selected) => handleFieldSelect(selected, undefined, key)}
                      placeholder='Select field'
                      searchable
                      loading={isDescribeFetching}
                    />
                  </div>
                  <Button variant='primaryGhost' className='p-2' onClick={() => handleRemoveSavedField(key)} title='Remove field'>
                    <i className='bi bi-trash' />
                  </Button>
                </div>
              )
            })}

            {tempRows.map((tempId) => (
              <div key={tempId} className='d-flex align-items-center gap-2'>
                <div className='flex-grow-1'>
                  <SelectDropdown
                    searchable
                    options={fieldOptions}
                    onOptionSelected={(selected) => handleFieldSelect(selected, tempId)}
                    placeholder='Select field'
                    initialSelectedOption={null}
                    loading={isDescribeFetching}
                  />
                </div>
                <Button variant='primaryGhost' className='p-2' onClick={() => handleRemoveTempRow(tempId)} title='Remove row'>
                  <i className='bi bi-trash' />
                </Button>
              </div>
            ))}
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Button type='button' variant='primaryGhost' className='border-0' onClick={handleAddField}>
            + Add field
          </Button>
        </Col>
      </Row>
    </Container>
  )
}
